import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyPlansApi } from 'features/company-plans/companyPlans.api';
import {
  COMPANY_STATUSES,
  companyDisplayName,
  displayCredit,
  invalidateCompanyPlanQueries,
  listItems,
  listMeta,
  maskedCode,
  memberLabel,
  planDisplayName,
  statusValue,
} from 'features/company-plans/companyPlans.helpers';
import { handleMutationError, StatusChip } from 'features/company-plans/CompanyPlanUi';
import { formatMoney } from 'features/company-plans/money';
import { Button } from 'shared/components/Button';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { FilterBar } from 'shared/components/FilterBar';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { useToast } from 'shared/components/Toast';
import { SelectField, TextField } from 'shared/forms/Fields';
import { formatDateTime, formatUserLabel } from 'shared/utilities/format';

const RELATED_PAGE_SIZE = 5;

function CompanyRelatedSection({
  title,
  caption,
  empty,
  viewAllTo,
  viewAllLabel,
  children,
}) {
  return (
    <section className="company-related-section">
      <div className="section-heading">
        <h2>{title}</h2>
        {viewAllTo ? <Link to={viewAllTo}>{viewAllLabel}</Link> : null}
      </div>
      {children || <EmptyState title={empty} icon={false} />}
    </section>
  );
}

export function CompaniesPage() {
  const { t, dir } = useI18n();
  const [filters, setFilters] = useState({ page: 1, per_page: 15, status: '', search: '' });
  const query = useQuery({
    queryKey: queryKeys.companies(filters),
    queryFn: ({ signal }) => companyPlansApi.listCompanies(filters, { signal }),
  });
  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('companyPlans.companiesTitle')} />
      <FilterBar resetLabel={t('actions.reset')} onReset={() => setFilters({ page: 1, per_page: 15, status: '', search: '' })}>
        <TextField
          id="company-search"
          label={t('actions.search')}
          value={filters.search}
          onChange={(value) => setFilters((current) => ({ ...current, search: value, page: 1 }))}
        />
        <SelectField
          id="company-status"
          label={t('companyPlans.status')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...COMPANY_STATUSES.map((status) => ({ value: status, label: t(`companyPlans.statuses.${status}`) }))]}
        />
      </FilterBar>
      {items.length === 0 ? <EmptyState title={t('empty.companies')} /> : (
        <ListTable caption={t('companyPlans.companiesTitle')} stickyActions>
          <thead>
            <tr>
              <th>{t('companyPlans.companyName')}</th>
              <th>{t('companyPlans.owner')}</th>
              <th>{t('companyPlans.status')}</th>
              <th>{t('companyPlans.activeEntitlements')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((company) => (
              <tr key={company.id}>
                <td>{companyDisplayName(company)}</td>
                <td>{formatUserLabel(company.owner, t)}</td>
                <td><StatusChip status={company.status} t={t} /></td>
                <td>{company.active_entitlements_count ?? '—'}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${company.id}`}
                  items={[detailsAction(paths.companyDetail(company.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function CompanyDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.companiesManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const companyFilters = { company_id: id, page: 1, per_page: 1 };
  const relatedFilters = { company_id: id, page: 1, per_page: RELATED_PAGE_SIZE };

  const query = useQuery({
    queryKey: queryKeys.company(id),
    queryFn: ({ signal }) => companyPlansApi.showCompany(id, { signal }),
  });
  const purchasesCountQuery = useQuery({
    queryKey: queryKeys.planPurchases(companyFilters),
    queryFn: ({ signal }) => companyPlansApi.listPurchases(companyFilters, { signal }),
  });
  const activeEntitlementsCountQuery = useQuery({
    queryKey: queryKeys.companyEntitlements({ ...companyFilters, status: 'active' }),
    queryFn: ({ signal }) => companyPlansApi.listEntitlements({ ...companyFilters, status: 'active' }, { signal }),
  });
  const codeBatchesCountQuery = useQuery({
    queryKey: queryKeys.accessCodeBatches(companyFilters),
    queryFn: ({ signal }) => companyPlansApi.listCodeBatches(companyFilters, { signal }),
  });
  const accessCodesCountQuery = useQuery({
    queryKey: queryKeys.accessCodes({ ...companyFilters, status: 'claimed' }),
    queryFn: ({ signal }) => companyPlansApi.listAccessCodes({ ...companyFilters, status: 'claimed' }, { signal }),
  });
  const sponsoredCountQuery = useQuery({
    queryKey: queryKeys.sponsoredAssessments({ ...companyFilters, status: 'completed' }),
    queryFn: ({ signal }) => companyPlansApi.listSponsoredAssessments({ ...companyFilters, status: 'completed' }, { signal }),
  });
  const purchasesQuery = useQuery({
    queryKey: queryKeys.planPurchases(relatedFilters),
    queryFn: ({ signal }) => companyPlansApi.listPurchases(relatedFilters, { signal }),
  });
  const entitlementsQuery = useQuery({
    queryKey: queryKeys.companyEntitlements({ ...relatedFilters, status: 'active' }),
    queryFn: ({ signal }) => companyPlansApi.listEntitlements({ ...relatedFilters, status: 'active' }, { signal }),
  });
  const batchesQuery = useQuery({
    queryKey: queryKeys.accessCodeBatches(relatedFilters),
    queryFn: ({ signal }) => companyPlansApi.listCodeBatches(relatedFilters, { signal }),
  });
  const codesQuery = useQuery({
    queryKey: queryKeys.accessCodes(relatedFilters),
    queryFn: ({ signal }) => companyPlansApi.listAccessCodes(relatedFilters, { signal }),
  });
  const sponsoredQuery = useQuery({
    queryKey: queryKeys.sponsoredAssessments({ ...relatedFilters, status: 'completed' }),
    queryFn: ({ signal }) => companyPlansApi.listSponsoredAssessments({ ...relatedFilters, status: 'completed' }, { signal }),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => companyPlansApi.setCompanyStatus(id, status),
    onSuccess: async () => {
      await invalidateCompanyPlanQueries(queryClient);
      toast.success(t('status.updated'));
    },
    onError: (error) => handleMutationError(error, { t, toast }),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const company = query.data;
  const status = statusValue(company.status);
  const members = company.members || company.memberships || [];
  const purchasesTotal = purchasesCountQuery.data?.meta?.total;
  const activeEntitlementsTotal = activeEntitlementsCountQuery.data?.meta?.total;
  const batchesTotal = codeBatchesCountQuery.data?.meta?.total;
  const claimedCodesTotal = accessCodesCountQuery.data?.meta?.total;
  const completedSponsoredTotal = sponsoredCountQuery.data?.meta?.total;
  const purchases = listItems(purchasesQuery.data);
  const entitlements = listItems(entitlementsQuery.data);
  const batches = listItems(batchesQuery.data);
  const codes = listItems(codesQuery.data);
  const sponsored = listItems(sponsoredQuery.data);
  const creditTotals = entitlements.reduce((totals, entitlement) => ({
    total_credits: totals.total_credits + Number(entitlement.total_credits || 0),
    available_credits: totals.available_credits + Number(entitlement.available_credits || 0),
  }), { total_credits: 0, available_credits: 0 });
  const showCreditTotals = entitlements.length > 0
    && (activeEntitlementsTotal == null || activeEntitlementsTotal <= RELATED_PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title={companyDisplayName(company)}
        backTo={paths.companies}
        actions={canManage ? (
          <Button
            tone={status === 'active' ? 'danger' : 'primary'}
            loading={statusMutation.isPending}
            onClick={async () => {
              const next = status === 'active' ? 'suspended' : 'active';
              const confirmed = await confirm({
                title: next === 'suspended' ? t('companyPlans.suspendCompany') : t('companyPlans.activateCompany'),
                body: next === 'suspended' ? t('companyPlans.suspendCompanyBody') : t('companyPlans.activateCompanyBody'),
                tone: next === 'suspended' ? 'danger' : 'primary',
              });
              if (confirmed) statusMutation.mutate(next);
            }}
          >
            {status === 'active' ? t('companyPlans.suspendCompany') : t('companyPlans.activateCompany')}
          </Button>
        ) : null}
      />
      {status === 'suspended' ? <p className="warning-banner" role="status">{t('companyPlans.suspendCompanyBody')}</p> : null}
      <dl className="definition-grid">
        <div className="definition-row"><dt>{t('companyPlans.legalName')}</dt><dd>{company.legal_name || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.displayName')}</dt><dd>{company.display_name || '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.owner')}</dt><dd>{formatUserLabel(company.owner, t)}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.status')}</dt><dd><StatusChip status={status} t={t} /></dd></div>
        <div className="definition-row"><dt>{t('companyPlans.purchases')}</dt><dd>{purchasesTotal ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.activeEntitlements')}</dt><dd>{activeEntitlementsTotal ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.creditTotals')}</dt><dd>{showCreditTotals ? creditTotals.total_credits : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.availableCredits')}</dt><dd>{showCreditTotals ? creditTotals.available_credits : '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.generatedCodes')}</dt><dd>{batchesTotal ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.claimedCodes')}</dt><dd>{claimedCodesTotal ?? '—'}</dd></div>
        <div className="definition-row"><dt>{t('companyPlans.completedSponsored')}</dt><dd>{completedSponsoredTotal ?? '—'}</dd></div>
      </dl>
      <h2>{t('companyPlans.members')}</h2>
      {members.length === 0 ? <EmptyState title={t('empty.generic')} icon={false} /> : (
        <ListTable caption={t('companyPlans.members')}>
          <thead>
            <tr>
              <th>{t('companyPlans.members')}</th>
              <th>{t('companyPlans.role')}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id || member.user_id}>
                <td>{memberLabel(member, t)}</td>
                <td>{t(`companyPlans.roles.${statusValue(member.role) || 'viewer'}`)}</td>
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <CompanyRelatedSection
        title={t('companyPlans.purchases')}
        empty={t('empty.purchases')}
        viewAllTo={`${paths.planPurchases}?company_id=${id}`}
        viewAllLabel={t('actions.viewAll')}
      >
        {purchases.length > 0 ? (
          <ListTable caption={t('companyPlans.purchases')} stickyActions>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('nav.companyPlans')}</th>
                <th>{t('companyPlans.paymentStatus')}</th>
                <th>{t('companyPlans.price')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {purchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td>{purchase.id}</td>
                  <td>{planDisplayName(purchase.plan || purchase, locale)}</td>
                  <td><StatusChip status={purchase.status} t={t} /></td>
                  <td>{formatMoney(purchase.total_minor, purchase.currency, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${purchase.id}`}
                    items={[detailsAction(paths.planPurchaseDetail(purchase.id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </ListTable>
        ) : null}
      </CompanyRelatedSection>
      <CompanyRelatedSection
        title={t('companyPlans.activeEntitlements')}
        empty={t('empty.entitlements')}
        viewAllTo={`${paths.companyEntitlements}?company_id=${id}`}
        viewAllLabel={t('actions.viewAll')}
      >
        {entitlements.length > 0 ? (
          <ListTable caption={t('companyPlans.activeEntitlements')} stickyActions>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('companyPlans.totalCredits')}</th>
                <th>{t('companyPlans.availableCredits')}</th>
                <th>{t('companyPlans.status')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {entitlements.map((entitlement) => (
                <tr key={entitlement.id}>
                  <td>{entitlement.id}</td>
                  <td>{displayCredit(entitlement, 'total_credits')}</td>
                  <td>{displayCredit(entitlement, 'available_credits')}</td>
                  <td><StatusChip status={entitlement.status} t={t} /></td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${entitlement.id}`}
                    items={[detailsAction(paths.companyEntitlementDetail(entitlement.id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </ListTable>
        ) : null}
      </CompanyRelatedSection>
      <CompanyRelatedSection
        title={t('nav.companyCodeBatches')}
        empty={t('empty.codeBatches')}
        viewAllTo={`${paths.companyCodeBatches}?company_id=${id}`}
        viewAllLabel={t('actions.viewAll')}
      >
        {batches.length > 0 ? (
          <ListTable caption={t('nav.companyCodeBatches')} stickyActions>
            <thead>
              <tr>
                <th>{t('companyPlans.batchOrCode')}</th>
                <th>{t('companyPlans.generatedCodes')}</th>
                <th>{t('companyPlans.createdAt')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td>{batch.label || `#${batch.id}`}</td>
                  <td>{batch.codes_count ?? batch.quantity ?? '—'}</td>
                  <td>{formatDateTime(batch.created_at, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${batch.id}`}
                    items={[detailsAction(paths.companyCodeBatchDetail(batch.id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </ListTable>
        ) : null}
      </CompanyRelatedSection>
      <CompanyRelatedSection
        title={t('nav.companyAccessCodes')}
        empty={t('empty.accessCodes')}
        viewAllTo={`${paths.companyAccessCodes}?company_id=${id}`}
        viewAllLabel={t('actions.viewAll')}
      >
        {codes.length > 0 ? (
          <ListTable caption={t('nav.companyAccessCodes')} stickyActions>
            <thead>
              <tr>
                <th>{t('companyPlans.batchOrCode')}</th>
                <th>{t('companyPlans.status')}</th>
                <th>{t('companyPlans.createdAt')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id}>
                  <td className="code-masked">{maskedCode(code)}</td>
                  <td><StatusChip status={code.status} t={t} /></td>
                  <td>{formatDateTime(code.created_at, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${code.id}`}
                    items={[detailsAction(paths.companyAccessCodeDetail(code.id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </ListTable>
        ) : null}
      </CompanyRelatedSection>
      <CompanyRelatedSection
        title={t('companyPlans.completedSponsored')}
        empty={t('empty.sponsoredAssessments')}
        viewAllTo={`${paths.sponsoredAssessments}?company_id=${id}`}
        viewAllLabel={t('actions.viewAll')}
      >
        {sponsored.length > 0 ? (
          <ListTable caption={t('companyPlans.sponsoredTitle')} stickyActions>
            <thead>
              <tr>
                <th>{t('companyPlans.participantName')}</th>
                <th>{t('companyPlans.profileCode')}</th>
                <th>{t('companyPlans.completedAt')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {sponsored.map((item) => (
                <tr key={item.attempt_id || item.id}>
                  <td>{formatUserLabel(item.participant || item.user, t)}</td>
                  <td>{item.profile_code || '—'}</td>
                  <td>{formatDateTime(item.completed_at, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${item.attempt_id || item.id}`}
                    items={[detailsAction(paths.sponsoredAssessmentResult(item.attempt_id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </ListTable>
        ) : null}
      </CompanyRelatedSection>
    </div>
  );
}
